import { API_URL } from "@/config/constants";
import { buildUrl } from "@/utils";
import axios from "axios";
import { IFilter, Page } from "../api.types";
import {
    IClauseTestDefinition,
    IClauseTestDefinitionCreate,
} from "../clause-test-definitions";
import { IScope } from "../scopes";
import {
    IClause,
    IClauseCreate,
    IClauseExample,
    IClauseExampleCreate,
    IClauseExampleUpdate,
    IClauseImportStats,
    IClauseUpdate,
    IClauseGuidanceResponse,
    IOrganizationClauseGuidance,
    IOrganizationClauseGuidanceCreate,
    IOrganizationClauseGuidanceUpdate,
} from "./clauses.types";

const API = `${API_URL}/clauses`;

export const clauseGuidanceQueryKey = (id: number) =>
    ["frontend-clause-guidance", id] as const;

export const createClause = async (clauseCreate: IClauseCreate) => {
    const result = await axios.post<IClause>(`${API}`, clauseCreate);
    return result.data;
};

export const getAllClauses = async (
    page: number = 1,
    size: number = 50,
    filter?: IFilter,
    clauseChecklistId?: number,
    search?: string | null
) => {
    const url = buildUrl(API, {
        page,
        size,
        ...filter,
        clause_checklist_id: clauseChecklistId,
        search,
    });
    const result = await axios.get<Page<IClause>>(url);
    return result.data;
};

export const bulkImportClauses = async (
    objectName: string,
    preprocessOnly: boolean = false
) => {
    const result = await axios.post<IClauseImportStats>(
        `${API}/import/${objectName}?preprocess_only=${preprocessOnly}`
    );
    return result.data;
};

export const getClauseById = async (id: number) => {
    const result = await axios.get<IClause>(`${API}/${id}`);
    return result.data;
};

export const exportClauses = async (format: "csv" | "xlsx" = "csv") => {
    const result = await axios.get(`${API}/export?type=${format}`, {
        responseType: "blob",
    });
    return result.data;
};

export const updateClauseById = async (
    id: number,
    clauseUpdate: IClauseUpdate
) => {
    const result = await axios.put<IClause>(`${API}/${id}`, clauseUpdate);
    return result.data;
};

export const deleteClauseById = async (id: number) => {
    const result = await axios.delete(`${API}/${id}`);
    return result.data;
};

export const addExamplesToClause = async (
    id: number,
    clauseExampleCreate: IClauseExampleCreate
) => {
    const result = await axios.post<IClauseExample[]>(
        `${API}/${id}/examples`,
        clauseExampleCreate
    );
    return result.data;
};

export const deleteExamples = async (
    clauseId: number,
    exampleIds: number[]
) => {
    const params = exampleIds.map((id) => `example_ids=${id}`).join("&");

    const result = await axios.delete(`${API}/${clauseId}/examples?${params}`);
    return result.data;
};

export const updateExample = async (
    clauseId: number,
    exampleId: number,
    exampleUpdate: IClauseExampleUpdate
) => {
    const result = await axios.put<IClauseExample>(
        `${API}/${clauseId}/examples/${exampleId}`,
        exampleUpdate
    );
    return result.data;
};

export const createClauseTestDefinition = async (
    clauseId: number,
    clauseTestDefinitionCreate: IClauseTestDefinitionCreate
) => {
    const result = await axios.post<IClauseTestDefinition>(
        `${API}/${clauseId}/test-definitions`,
        clauseTestDefinitionCreate
    );
    return result.data;
};

export const getAvailableClausesForTestDefinition = async () => {
    const result = await axios.get<IClause[]>(
        `${API}/test-definitions/available`
    );
    return result.data;
};

export const getClauseUsage = async (id: number) => {
    const result = await axios.get<IScope>(`${API}/${id}/usage`);
    return result.data;
};

export const createOrganizationClauseGuidance = async (
    id: number,
    guidanceIn: IOrganizationClauseGuidanceCreate
) => {
    const result = await axios.post(
        `${API}/${id}/organization-guidance`,
        guidanceIn
    );
    return result.data;
};

export const updateOrganizationClauseGuidance = async (
    id: number,
    guidanceIn: IOrganizationClauseGuidanceUpdate
) => {
    const result = await axios.put(
        `${API}/${id}/organization-guidance`,
        guidanceIn
    );
    return result.data;
};

export const deleteOrganizationClauseGuidance = async (id: number) => {
    const result = await axios.delete(`${API}/${id}/organization-guidance`);
    return result.data;
};

export const getOrganizationClauseGuidance = async (id: number) => {
    const result = await axios.get<IOrganizationClauseGuidance | null>(
        `${API}/${id}/organization-guidance`
    );
    return result.data;
};

export const getClauseGuidance = async (id: number) => {
    const result = await axios.get<IClauseGuidanceResponse>(
        `${API}/${id}/guidance`
    );
    return result.data;
};
